/**
 * R159zz · Feb 2026 · Settings › Projects · Status Catalogue
 * ------------------------------------------------------------------
 * Workspace-level rename + colour override for the 12 canonical
 * project statuses. Only workspace owners / admins / super-admins can
 * save (server-enforced).
 *
 *   GET    /workspaces/{ws}/settings/project-statuses  → merged map
 *   PUT    /workspaces/{ws}/settings/project-statuses  → save
 *   DELETE /workspaces/{ws}/settings/project-statuses/{key} → reset one
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Palette } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";
import { DEFAULT_STATUS_META, CANONICAL_STATUS_KEYS } from "@/hooks/useProjectStatuses";

// Curated palette of tailwind class triplets so admins pick from
// pre-approved on-brand colours instead of typing raw class names.
const COLOR_PRESETS = [
  { key: "emerald", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", label: "Emerald" },
  { key: "green_deep", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Green · Soft" },
  { key: "blue", cls: "bg-blue-100 text-blue-700 border-blue-300", label: "Blue" },
  { key: "sky", cls: "bg-sky-100 text-sky-700 border-sky-300", label: "Sky" },
  { key: "indigo", cls: "bg-indigo-100 text-indigo-700 border-indigo-300", label: "Indigo" },
  { key: "violet", cls: "bg-violet-100 text-violet-700 border-violet-300", label: "Violet" },
  { key: "amber", cls: "bg-amber-100 text-amber-700 border-amber-300", label: "Amber" },
  { key: "amber_soft", cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Amber · Soft" },
  { key: "orange", cls: "bg-orange-100 text-orange-700 border-orange-300", label: "Orange" },
  { key: "rose", cls: "bg-rose-100 text-rose-700 border-rose-300", label: "Rose" },
  { key: "pink", cls: "bg-pink-100 text-pink-700 border-pink-300", label: "Pink" },
  { key: "slate", cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Slate" },
  { key: "neutral", cls: "bg-neutral-100 text-neutral-500 border-neutral-300", label: "Neutral" },
];

function ColourPicker({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1 max-w-md" data-testid={testid}>
      {COLOR_PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onChange(p.cls)}
          className={`h-6 px-2 border text-[10px] uppercase tracking-widest ${p.cls} ${value === p.cls ? "ring-2 ring-primary ring-offset-1" : "opacity-80 hover:opacity-100"}`}
          title={p.label}
          data-testid={`${testid}-opt-${p.key}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export default function ProjectStatusesSettings() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/settings/project-statuses`);
      const map = data?.statuses || {};
      const local = {};
      CANONICAL_STATUS_KEYS.forEach((k) => {
        local[k] = {
          label: map[k]?.label || DEFAULT_STATUS_META[k].label,
          cls:   map[k]?.cls   || DEFAULT_STATUS_META[k].cls,
          is_default: !!map[k]?.is_default,
        };
      });
      setRows(local);
      setDirty(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load status catalogue"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const patch = (key, field, value) => {
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value, is_default: false } }));
    setDirty(true);
  };

  const resetOne = async (key) => {
    if (!confirm(`Reset "${rows[key]?.label}" back to the built-in default?`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/settings/project-statuses/${key}`);
      toast.success("Reset to default");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reset failed"));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { statuses: {} };
      CANONICAL_STATUS_KEYS.forEach((k) => {
        const r = rows[k];
        if (!r) return;
        const changed = r.label !== DEFAULT_STATUS_META[k].label || r.cls !== DEFAULT_STATUS_META[k].cls;
        if (changed) payload.statuses[k] = { label: r.label, cls: r.cls };
      });
      await api.put(`/workspaces/${activeWorkspaceId}/settings/project-statuses`, payload);
      toast.success("Status catalogue saved");
      setDirty(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <PageHeader
        title="Project statuses"
        subtitle="Rename each project status and pick a chip colour. Changes flow to the Planning Board card picker, Projects list, Project Overview form, filters and Present-mode pills."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none h-9" onClick={load} disabled={loading || saving} data-testid="project-statuses-refresh">
              <RotateCcw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button className="rounded-none h-9" onClick={save} disabled={!dirty || saving} data-testid="project-statuses-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save changes
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="border border-border">
          <div className="grid grid-cols-[140px,1fr,auto] gap-3 px-3 py-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            <span>Status key</span>
            <span>Label · Colour</span>
            <span>&nbsp;</span>
          </div>
          {CANONICAL_STATUS_KEYS.map((k) => {
            const r = rows[k] || DEFAULT_STATUS_META[k];
            const isDefault = r.label === DEFAULT_STATUS_META[k].label && r.cls === DEFAULT_STATUS_META[k].cls;
            return (
              <div key={k} className="grid grid-cols-[140px,1fr,auto] gap-3 px-3 py-3 border-b border-border last:border-b-0 items-start"
                   data-testid={`project-status-row-${k}`}>
                <div className="pt-1.5">
                  <code className="text-[10px] font-mono text-muted-foreground">{k}</code>
                  {!isDefault && <div className="text-[9px] uppercase tracking-widest text-amber-600 mt-0.5">Custom</div>}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={r.label}
                      onChange={(e) => patch(k, "label", e.target.value)}
                      className="h-8 text-xs max-w-xs"
                      data-testid={`project-status-label-${k}`}
                      maxLength={40}
                    />
                    <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${r.cls}`}
                          data-testid={`project-status-preview-${k}`}>
                      {r.label}
                    </span>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <Palette className="w-3.5 h-3.5 mt-1 text-muted-foreground shrink-0" />
                    <ColourPicker value={r.cls} onChange={(v) => patch(k, "cls", v)} testid={`project-status-color-${k}`} />
                  </div>
                </div>
                <div className="pt-1">
                  {!isDefault && (
                    <Button variant="ghost" size="sm" className="rounded-none h-7 text-[10px] uppercase tracking-widest"
                            onClick={() => resetOne(k)}
                            data-testid={`project-status-reset-${k}`}>
                      Reset
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-3">
        Only workspace owners and admins can edit this catalogue. The internal status keys (
        <code className="text-[10px] font-mono">active</code>, <code className="text-[10px] font-mono">in_progress</code>…)
        stay the same so filters, dashboards and integrations continue to work — only the
        display label and chip colour change.
      </p>
    </div>
  );
}
